import React, { useMemo } from "react";
import {
  ConstrainMode,
  ScrollablePane,
  ScrollbarVisibility,
  Sticky,
  StickyPositionType,
  Stack,
  Text,
} from "@fluentui/react";
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
} from "@fluentui/react/lib/DetailsList";
import { useTheme } from "../../../../theme/OAS365Theme";
import { useGridStyle } from "../../../../theme/styles/GridStyle";
import DocumentsPagination from "./DocumentsPagination";
import Loader from "../../../../components/LoadSpinner";
import { EMPTY_RESULTS } from "../../../../redux/constants/messageTypes";
import noContentIcon from "../../../../assets/svg/noSearchResults.svg";
import luErrorImage from "../../../../assets/advSearchError.svg";
const getKey = (item) => item.key;

const emptyStateImageStyle = {
  width: 240,
  height: 240,
  objectFit: "contain",
  marginBottom: 16,
};

const DocumentsTable = ({
  loading = false,
  isError = false,
  errorMessage = "",
  items = [],
  columns = [],
  selection,
  paginationLoading = false,
  pageSize,
  documents = [],
  selectedPageIndex = 0,
  onPageIndexChange,
  hidePagination = false,
  stickyHeader = true,
  disableScrollablePane = false,
  disableVirtualization = false,
}) => {
  const theme = useTheme();
  const gridThemeStyle = useGridStyle();
  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
  const sbWidth = 10;
  const sbHeight = 10;
  const isEmpty = !loading && !isError && (!items || items.length === 0);

  const minTableWidth = useMemo(
    () =>
      columns.reduce(
        (sum, col) => sum + (col.minWidth ?? col.maxWidth ?? 0),
        0,
      ),
    [columns],
  );

  const detailsListStyles = useMemo(
    () => ({
      ...gridThemeStyle.detailListStyle,
      root: [
        gridThemeStyle.detailListStyle?.root,
        {
          minWidth: minTableWidth > 0 ? minTableWidth : undefined,
          overflowX: "visible",
        },
      ],
    }),
    [gridThemeStyle.detailListStyle, minTableWidth],
  );

  const renderDetailsList = () => (
    <DetailsList
      styles={detailsListStyles}
      items={items}
      compact={false}
      columns={columns}
      selectionMode={SelectionMode.none}
      getKey={getKey}
      layoutMode={DetailsListLayoutMode.fixedColumns}
      constrainMode={ConstrainMode.unconstrained}
      isHeaderVisible={true}
      selection={selection}
      selectionPreservedOnEmptyClick={true}
      enterModalSelectionOnTouch={true}
      ariaLabelForSelectionColumn="Toggle selection"
      ariaLabelForSelectAllCheckbox="Toggle selection for all items"
      checkButtonAriaLabel="select row"
      visibility="false"
      onShouldVirtualize={() => !disableVirtualization}
      onRenderDetailsHeader={(headerProps, defaultRender) =>
        stickyHeader ? (
          <Sticky
            stickyPosition={StickyPositionType.Header}
            isScrollSynced={true}
            stickyBackgroundColor={theme.colors.backgroundColor}
          >
            {defaultRender({
              ...headerProps,
              styles: gridThemeStyle.headerStyle,
            })}
          </Sticky>
        ) : (
          defaultRender({
            ...headerProps,
            styles: gridThemeStyle.headerStyle,
          })
        )
      }
    />
  );

  const tableContent = (
    <>
      {loading ? (
        <Stack
          verticalAlign="center"
          horizontalAlign="center"
          styles={{
            root: {
              minHeight: 200,
              background: theme.colors.backgroundColor,
            },
          }}
        >
          <Loader />
        </Stack>
      ) : isError ? (
        <Stack
          verticalAlign="center"
          horizontalAlign="center"
          styles={{
            root: {
              minHeight: 280,
              background: theme.colors.backgroundColor,
              padding: 24,
            },
          }}
          tokens={{ childrenGap: 8 }}
        >
          <img
            src={luErrorImage}
            alt=""
            style={emptyStateImageStyle}
            aria-hidden
          />
          <Text
            variant="medium"
            styles={{
              root: {
                color: theme.colors?.fontColor ?? "inherit",
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
              },
            }}
          >
            {errorMessage || "Something went wrong. Please try again."}
          </Text>
        </Stack>
      ) : isEmpty ? (
        <Stack
          verticalAlign="center"
          horizontalAlign="center"
          styles={{
            root: {
              height: "100%",
              minHeight: 280,
              background: theme.colors.backgroundColor,
              padding: 24,
            },
          }}
          tokens={{ childrenGap: 8 }}
        >
          <Text
            variant="medium"
            styles={{
              root: {
                color: "#4c8d6a",
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
              },
            }}
          >
            {EMPTY_RESULTS}
          </Text>
          <img
            src={noContentIcon}
            alt=""
            style={emptyStateImageStyle}
            aria-hidden
          />
        </Stack>
      ) : disableScrollablePane ? (
        renderDetailsList()
      ) : (
        <ScrollablePane
          scrollbarVisibility={ScrollbarVisibility.auto}
          styles={{
            root: {
              overflowX: "auto",
              selectors: {
                ".ms-ScrollablePane--contentContainer": {
                  overflowX: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
                  "-ms-overflow-style": "auto",
                },
                ".ms-ScrollablePane--contentContainer::-webkit-scrollbar": {
                  width: sbWidth,
                  height: sbHeight,
                  display: "block",
                },
                ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track": {
                  background: scrollbarTrack,
                },
                ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb": {
                  background: scrollbarThumb,
                  borderRadius: sbWidth,
                  border: `2px solid ${scrollbarTrack}`,
                  minHeight: 40,
                },
                ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb:hover":
                  {
                    background: isDarkMode ? "#9a9a9a" : "#b3b3b3",
                  },
                ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-button":
                  {
                    display: "none",
                    height: 0,
                    width: 0,
                  },
              },
            },
          }}
        >
          {renderDetailsList()}
        </ScrollablePane>
      )}
    </>
  );

  if (hidePagination) {
    return tableContent;
  }

  // Show pagination area only after list has finished loading; then show loader if count is pending, else pagination
  const showPaginationArea = !loading;
  const paginationSection = showPaginationArea && (
    <Stack.Item>
      <Stack
        styles={{
          root: {
            background: theme.colors.backgroundColor,
            padding: 5,
          },
        }}
      >
        <Stack.Item align="center">
          {paginationLoading ? (
            <Loader />
          ) : (
            <DocumentsPagination
              navigationLoading={paginationLoading}
              pageSize={pageSize}
              documents={documents}
              selectedPageIndex={selectedPageIndex}
              onPageIndexChange={onPageIndexChange}
            />
          )}
        </Stack.Item>
      </Stack>
    </Stack.Item>
  );

  return (
    <Stack
      enableScopedSelectors
      grow
      styles={{
        root: {
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        },
      }}
    >
      <Stack.Item
        grow
        styles={{
          root: {
            overflow: "hidden",
            minHeight: 0,
          },
        }}
      >
        {tableContent}
      </Stack.Item>
      {paginationSection}
    </Stack>
  );
};

export default DocumentsTable;
